/* oxlint-disable react/style-prop-object */
/* eslint-disable solid/style-prop */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import {
  ChevronRight,
  Folder,
  FolderOpen,
  List as ListIcon,
  MoreVertical,
  Plus,
  Search,
  Users,
  X,
} from "lucide-solid";
import { cn, type ListGroup, type ListWithMeta } from "@listto/shared";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { Deferred } from "@/components/ui/deferred";
import { useAppLayoutContext } from "@/routes/_app/route";
import { CounterBadge } from "@/components/ui/counter-badge";

// Helper styles for list icon squirrel badges
const getBgStyle = (color: string | null): string => {
  if (!color) {
    return "";
  }
  if (color.startsWith("#")) {
    return `background-color: ${color}26;`;
  }
  return `background-color: ${color}; opacity: 0.15;`;
};

const getTextColorStyle = (color: string | null): string => {
  if (!color) {
    return "";
  }
  return `color: ${color};`;
};

const ANIMATION_STAGGER_DELAY_SKELETON_MS = 60;
const ANIMATION_STAGGER_DELAY_GROUP_MS = 50;
const DEBOUNCE_DELAY_MS = 300;
const COLLAPSE_UNMOUNT_DELAY_MS = 250;
const EXPAND_PAINT_DELAY_MS = 20;

interface DashboardListsProps {
  lists: ListWithMeta[];
  groups: ListGroup[];
  todoCounts: Map<string, number> | undefined;
  onAddList: (groupId?: string) => void;
  isLoading?: boolean;
  isCountsLoading?: boolean;
}

export function DashboardLists(props: DashboardListsProps) {
  const { t } = useI18n();
  const layout = useAppLayoutContext();
  const [animationActive, setAnimationActive] = createSignal(true);

  onMount(() => {
    const ANIMATION_DURATION_MS = 800;
    setTimeout(() => {
      setAnimationActive(false);
    }, ANIMATION_DURATION_MS);
  });

  const [collapsedGroups, setCollapsedGroups] = createSignal<Record<string, boolean>>({});
  const [displayQuery, setDisplayQuery] = createSignal("");
  const [searchQuery, setSearchQuery] = createSignal("");

  createEffect(() => {
    const val = displayQuery();
    const handle = setTimeout(() => {
      setSearchQuery(val);
    }, DEBOUNCE_DELAY_MS);

    onCleanup(() => {
      clearTimeout(handle);
    });
  });

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const filteredLists = createMemo(() => {
    const q = searchQuery().trim().toLowerCase();
    if (!q) {
      return props.lists;
    }

    // O(G) matching groups lookup map
    const matchingGroupIds = new Set<string>();
    for (const group of props.groups) {
      if (group.name?.toLowerCase().includes(q)) {
        matchingGroupIds.add(group.id);
      }
    }

    // O(L) filtering with O(1) set lookups
    return props.lists.filter((list) => {
      const listMatches = list.name ? list.name.toLowerCase().includes(q) : false;
      if (listMatches) {
        return true;
      }
      const gid = list.group_id;
      return gid ? matchingGroupIds.has(gid) : false;
    });
  });

  // 1. Group lists in O(L) using a HashMap inside a Memo to avoid O(G * L) filter overhead
  const listsByGroup = createMemo(() => {
    const map = new Map<string | null, ListWithMeta[]>();
    for (const list of filteredLists()) {
      const gid = list.group_id ?? null;
      let arr = map.get(gid);
      if (!arr) {
        arr = [];
        map.set(gid, arr);
      }
      arr.push(list);
    }
    return map;
  });

  const filteredGroups = createMemo(() => {
    const q = searchQuery().trim().toLowerCase();
    if (!q) {
      return props.groups;
    }
    return props.groups.filter((group) => {
      const folderMatches = group.name ? group.name.toLowerCase().includes(q) : false;
      const hasMatchingLists = (listsByGroup().get(group.id) ?? []).length > 0;
      return folderMatches || hasMatchingLists;
    });
  });

  const allCollapsed = () => {
    const groups = filteredGroups();
    if (groups.length === 0) {
      return false;
    }
    return groups.every((g) => collapsedGroups()[g.id] === true);
  };

  const toggleAllGroups = () => {
    const groups = filteredGroups();
    const shouldCollapse = !allCollapsed();
    const newCollapsed: Record<string, boolean> = {};
    for (const g of groups) {
      newCollapsed[g.id] = shouldCollapse;
    }
    setCollapsedGroups(newCollapsed);
  };

  const hasFilteredResults = () => {
    const q = searchQuery().trim();
    if (!q) {
      return true;
    }
    return filteredLists().length > 0 || filteredGroups().length > 0;
  };

  const hasData = () => props.lists.length > 0 || props.groups.length > 0;
  const ungroupedLists = () => listsByGroup().get(null) ?? [];

  // --- Reactive Scroll Container Tracking for Virtualization ---
  let rootEl: HTMLDivElement | undefined = undefined;
  let scrollParent: HTMLElement | null = null;
  const DEFAULT_CONTAINER_HEIGHT = 800;
  const [scrollTop, setScrollTop] = createSignal(0);
  const [containerHeight, setContainerHeight] = createSignal(DEFAULT_CONTAINER_HEIGHT);

  onMount(() => {
    if (rootEl) {
      const el = rootEl.closest(".overflow-y-auto");
      if (el instanceof HTMLElement) {
        scrollParent = el;

        let rafHandle = 0;
        const handleScroll = () => {
          if (rafHandle) {
            return;
          }
          rafHandle = requestAnimationFrame(() => {
            rafHandle = 0;
            if (scrollParent) {
              setScrollTop(scrollParent.scrollTop);
            }
          });
        };

        const handleResize = () => {
          if (scrollParent) {
            setContainerHeight(scrollParent.clientHeight);
          }
        };

        scrollParent.addEventListener("scroll", handleScroll, { passive: true });

        const ro = new ResizeObserver(() => {
          handleResize();
        });
        ro.observe(scrollParent);

        // Initial measurements
        handleResize();
        setScrollTop(scrollParent.scrollTop);

        onCleanup(() => {
          if (rafHandle) {
            cancelAnimationFrame(rafHandle);
          }
          scrollParent?.removeEventListener("scroll", handleScroll);
          ro.disconnect();
        });
      }
    }
  });

  // --- Reusable Virtual Range Calculation ---
  const createVirtualRange = (
    cardRef: () => HTMLDivElement | undefined,
    items: () => ListWithMeta[],
    isCollapsed?: () => boolean,
  ) => {
    const memoRange = createMemo(() => {
      const listItems = items();
      const itemsCount = listItems.length;
      if (itemsCount === 0) {
        return { start: 0, end: 0, paddingTop: 0, paddingBottom: 0 };
      }

      if (isCollapsed?.()) {
        return { start: 0, end: 0, paddingTop: 0, paddingBottom: 0 };
      }

      const ref = cardRef();
      if (!ref || !scrollParent) {
        return { start: 0, end: itemsCount, paddingTop: 0, paddingBottom: 0 };
      }

      // Calculate offsetTop of card relative to the scrollable parent container
      let offsetTop = 0;
      let curr: HTMLElement | null = ref;
      while (curr && curr !== scrollParent) {
        offsetTop += curr.offsetTop;
        const parent: Element | null = curr.offsetParent;
        curr = parent instanceof HTMLElement ? parent : null;
      }

      const scrollT = scrollTop();
      const parentH = containerHeight();

      const ROW_HEIGHT = 52;
      const OVERSCAN = 6;
      const CARD_HEADER_HEIGHT = 56;

      const listTop = offsetTop + CARD_HEADER_HEIGHT;
      const listBottom = listTop + itemsCount * ROW_HEIGHT;

      // Calculate overlap between list content and the viewport
      const intersectionStart = Math.max(listTop, scrollT);
      const intersectionEnd = Math.min(listBottom, scrollT + parentH);

      if (intersectionStart >= intersectionEnd) {
        // Completely out of viewport
        return { start: 0, end: 0, paddingTop: 0, paddingBottom: itemsCount * ROW_HEIGHT };
      }

      const startScroll = intersectionStart - listTop;
      const endScroll = intersectionEnd - listTop;

      const start = Math.max(0, Math.floor(startScroll / ROW_HEIGHT) - OVERSCAN);
      const end = Math.min(itemsCount, Math.ceil(endScroll / ROW_HEIGHT) + OVERSCAN);

      const paddingTop = start * ROW_HEIGHT;
      const paddingBottom = Math.max(0, (itemsCount - end) * ROW_HEIGHT);

      return { start, end, paddingTop, paddingBottom };
    });
    return memoRange;
  };

  // Ungrouped card ref and range
  let ungroupedCardRef: HTMLDivElement | undefined = undefined;
  const ungroupedRange = createVirtualRange(() => ungroupedCardRef, ungroupedLists);
  const visibleUngroupedLists = () =>
    ungroupedLists().slice(ungroupedRange().start, ungroupedRange().end);

  const getTodoCount = (listId: string) => props.todoCounts?.get(listId) ?? 0;

  return (
    <div
      ref={(el) => {
        rootEl = el;
      }}
      class="space-y-6"
    >
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/10 pb-4">
        <div class="space-y-1">
          <h2 class="text-2xl font-bold tracking-tight text-foreground/90">{t(msg`My lists`)}</h2>
          <p class="text-xs text-muted-foreground">
            {t(msg`Manage folders and individual workspaces`)}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <Show when={filteredGroups().length > 0}>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAllGroups}
              class="gap-2 rounded-full px-4 text-muted-foreground hover:text-foreground hover:bg-foreground/5 font-semibold"
            >
              <Show when={allCollapsed()} fallback={<Folder class="h-4 w-4" />}>
                <FolderOpen class="h-4 w-4" />
              </Show>
              {allCollapsed() ? t(msg`Expand all`) : t(msg`Collapse all`)}
            </Button>
          </Show>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              props.onAddList();
            }}
            class="gap-2 rounded-full px-4 border-accent/30 hover:border-accent/60 text-accent hover:bg-accent/5 font-semibold"
          >
            <Plus class="h-4 w-4" />
            {t(msg`New list`)}
          </Button>
        </div>
      </div>

      {/* List and Folder Search Input Bar */}
      <Show when={hasData()}>
        <div class="relative w-full max-w-md animate-fade-in">
          <span class="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-muted-foreground/60">
            <Search class="h-4 w-4" />
          </span>
          <input
            type="text"
            placeholder={t(msg`Search lists or folders...`)}
            value={displayQuery()}
            onInput={(e) => setDisplayQuery(e.currentTarget.value)}
            class="w-full pl-10 pr-10 py-2.5 text-sm rounded-xl border border-border/45 bg-card/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:border-accent/50 text-foreground placeholder:text-muted-foreground/50 transition-all font-semibold"
          />
          <Show when={displayQuery().length > 0}>
            <button
              type="button"
              onClick={() => {
                setDisplayQuery("");
                setSearchQuery("");
              }}
              class="absolute inset-y-0 right-0 flex items-center pr-3.5 text-muted-foreground/60 hover:text-foreground transition-colors cursor-pointer"
            >
              <X class="h-4 w-4" />
            </button>
          </Show>
        </div>
      </Show>

      <Show
        when={hasData() || props.isLoading}
        fallback={
          <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/40 p-12 text-center bg-card/5 animate-scale-in">
            <ListIcon class="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
            <h3 class="text-base font-semibold">{t(msg`No lists`)}</h3>
            <p class="text-sm text-muted-foreground">
              {t(msg`Create one to organize your projects.`)}
            </p>
          </div>
        }
      >
        <Show
          when={!props.isLoading}
          fallback={
            <Deferred delayMs={800}>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                <For each={Array.from({ length: 3 })}>
                  {(_, index) => (
                    <div
                      class="rounded-2xl border border-border/40 bg-card/5 backdrop-blur-sm p-5 flex flex-col space-y-4 animate-pulse animate-slide-in-up will-change-[transform,opacity]"
                      style={`animation-delay: ${index() * ANIMATION_STAGGER_DELAY_SKELETON_MS}ms; animation-fill-mode: both;`}
                    >
                      <div class="flex items-center gap-3 pb-2.5 border-b border-border/10">
                        <Skeleton class="h-8 w-8 rounded-lg bg-muted/40" />
                        <Skeleton class="h-5 w-24 rounded bg-muted/40" />
                      </div>
                      <div class="space-y-3">
                        <div class="flex items-center justify-between">
                          <Skeleton class="h-4.5 w-1/2 rounded bg-muted/30" />
                          <Skeleton class="h-4 w-6 rounded bg-muted/30" />
                        </div>
                        <div class="flex items-center justify-between">
                          <Skeleton class="h-4.5 w-2/3 rounded bg-muted/30" />
                          <Skeleton class="h-4 w-6 rounded bg-muted/30" />
                        </div>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Deferred>
          }
        >
          <Show
            when={hasFilteredResults()}
            fallback={
              <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/40 p-12 text-center bg-card/5 animate-scale-in w-full">
                <Search class="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
                <h3 class="text-base font-semibold">{t(msg`No matches found`)}</h3>
                <p class="text-sm text-muted-foreground mb-4">
                  {(() => {
                    const query = searchQuery();
                    return t(msg`No lists or folders match '${query}'.`);
                  })()}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDisplayQuery("");
                    setSearchQuery("");
                  }}
                  class="rounded-full px-4 border-border/40 font-semibold"
                >
                  {t(msg`Clear search`)}
                </Button>
              </div>
            }
          >
            {/* Responsive layout: Grid on widescreen, single stack on mobile */}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Render Groups */}
              <For each={filteredGroups()}>
                {(group, index) => {
                  const groupLists = () => listsByGroup().get(group.id) ?? [];
                  const isCollapsed = () => collapsedGroups()[group.id] ?? false;

                  const [shouldRenderContent, setShouldRenderContent] =
                    createSignal(!isCollapsed());
                  const [isHeightExpanded, setIsHeightExpanded] = createSignal(!isCollapsed());

                  createEffect(() => {
                    const collapsed = isCollapsed();
                    let handle: ReturnType<typeof setTimeout> | undefined = undefined;

                    if (collapsed) {
                      setIsHeightExpanded(false);
                      handle = setTimeout(() => {
                        setShouldRenderContent(false);
                      }, COLLAPSE_UNMOUNT_DELAY_MS);
                    } else {
                      setShouldRenderContent(true);
                      handle = setTimeout(() => {
                        setIsHeightExpanded(true);
                      }, EXPAND_PAINT_DELAY_MS);
                    }

                    onCleanup(() => {
                      clearTimeout(handle);
                    });
                  });

                  let groupCardRef: HTMLDivElement | undefined = undefined;
                  const groupRange = createVirtualRange(
                    () => groupCardRef,
                    groupLists,
                    () => !shouldRenderContent(),
                  );
                  const visibleGroupLists = () =>
                    groupLists().slice(groupRange().start, groupRange().end);

                  return (
                    <div
                      ref={(el) => {
                        groupCardRef = el;
                      }}
                      class={cn(
                        "rounded-2xl border border-border/40 bg-card/5 backdrop-blur-sm p-4 flex flex-col space-y-1 self-start w-full",
                        animationActive() && "animate-slide-in-up will-change-[transform,opacity]",
                      )}
                      style={`animation-delay: ${index() * ANIMATION_STAGGER_DELAY_GROUP_MS}ms; animation-fill-mode: both;`}
                    >
                      {/* Folder Group Header Row */}
                      <div class="flex items-center justify-between gap-1 group/header relative pb-2 border-b border-border/10 mb-2">
                        <button
                          type="button"
                          onClick={() => {
                            toggleGroup(group.id);
                          }}
                          class="flex flex-1 items-center gap-3 py-2 pr-2 pl-1 group/group-row select-none bg-transparent border-none cursor-pointer text-left min-w-0"
                        >
                          <div class="flex h-8 w-8 items-center justify-center rounded-lg text-accent bg-accent/10 shrink-0">
                            <Show when={!isCollapsed()} fallback={<Folder class="h-4.5 w-4.5" />}>
                              <FolderOpen class="h-4.5 w-4.5" />
                            </Show>
                          </div>
                          <div class="flex-1 min-w-0">
                            <h3 class="text-base font-bold text-foreground/90 group-hover/group-row:text-accent transition-colors truncate">
                              <HighlightText text={group.name} query={searchQuery()} />
                            </h3>
                          </div>
                          <div class="flex items-center gap-2 shrink-0">
                            <span class="px-2 py-0.5 text-xs font-semibold rounded-md bg-accent/10 text-accent">
                              {groupLists().length}
                            </span>
                            <ChevronRight
                              class={cn(
                                "h-4 w-4 text-muted-foreground/60 transition-[transform,color] duration-200 ease-out will-change-transform",
                                !isCollapsed() && "rotate-90 text-accent",
                              )}
                            />
                          </div>
                        </button>

                        {/* Group Management Actions on Hover */}
                        <div class="flex items-center gap-0.5 shrink-0 opacity-0 group-hover/header:opacity-100 transition-opacity duration-200">
                          <Button
                            variant="ghost"
                            size="icon"
                            class="h-7 w-7 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                            onClick={() => {
                              layout.openEditGroupDialog(group);
                            }}
                            title={t(msg`Edit group`)}
                          >
                            <MoreVertical class="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            class="h-7 w-7 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                            onClick={() => {
                              props.onAddList(group.id);
                            }}
                            title={t(msg`Add list to group`)}
                          >
                            <Plus class="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Indented Lists inside this Group Card */}
                      <div
                        class={cn(
                          "grid transition-all duration-200 ease-out",
                          isHeightExpanded()
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0 pointer-events-none",
                        )}
                        inert={isCollapsed() ? true : undefined}
                      >
                        <div class="overflow-hidden min-h-0">
                          <div
                            class="flex flex-col space-y-1 transition-transform duration-200 ease-out origin-top pt-1 will-change-transform"
                            style={`transform: ${isCollapsed() ? "scaleY(0.95)" : "scaleY(1)"}; padding-top: ${groupRange().paddingTop}px; padding-bottom: ${groupRange().paddingBottom}px;`}
                          >
                            <For each={visibleGroupLists()}>
                              {(list) => {
                                const listIdx = () => groupLists().indexOf(list);
                                return (
                                  <ListRow
                                    list={list}
                                    todoCount={getTodoCount(list.id)}
                                    indent
                                    getBgStyle={getBgStyle}
                                    getTextColorStyle={getTextColorStyle}
                                    layout={layout}
                                    t={t}
                                    index={listIdx()}
                                    isCountsLoading={props.isCountsLoading}
                                    searchQuery={searchQuery()}
                                  />
                                );
                              }}
                            </For>

                            {/* Add list button inside group (indented) */}
                            <button
                              type="button"
                              onClick={() => {
                                props.onAddList(group.id);
                              }}
                              class="flex items-center gap-3 py-2.5 pr-3 pl-10 w-full text-left rounded-xl hover:bg-foreground/[0.02] text-muted-foreground/60 hover:text-muted-foreground transition-colors group/add-row cursor-pointer"
                            >
                              <div class="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-muted-foreground/30 group-hover/add-row:border-muted-foreground/50 shrink-0">
                                <Plus class="h-4 w-4" />
                              </div>
                              <span class="text-xs font-semibold">{t(msg`Add list`)}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }}
              </For>

              {/* Render Ungrouped Lists as their own Card Container */}
              <Show when={ungroupedLists().length > 0}>
                <div
                  ref={(el) => {
                    ungroupedCardRef = el;
                  }}
                  class={cn(
                    "rounded-2xl border border-border/40 bg-card/5 backdrop-blur-sm p-4 flex flex-col space-y-1 self-start w-full",
                    animationActive() && "animate-slide-in-up will-change-[transform,opacity]",
                  )}
                  style={`animation-delay: ${filteredGroups().length * ANIMATION_STAGGER_DELAY_GROUP_MS}ms; animation-fill-mode: both;`}
                >
                  <div class="flex items-center gap-3 py-2 pr-2 pl-1 border-b border-border/10 mb-2">
                    <div class="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground bg-foreground/5 shrink-0">
                      <ListIcon class="h-4.5 w-4.5" />
                    </div>
                    <h3 class="flex-1 text-base font-bold text-foreground/80">
                      {t(msg`Ungrouped`)}
                    </h3>
                    <span class="px-2 py-0.5 text-xs font-semibold rounded-md bg-muted-foreground/10 text-muted-foreground">
                      {ungroupedLists().length}
                    </span>
                  </div>

                  <div class="flex flex-col space-y-1">
                    <div
                      class="flex flex-col space-y-1"
                      style={`padding-top: ${ungroupedRange().paddingTop}px; padding-bottom: ${ungroupedRange().paddingBottom}px;`}
                    >
                      <For each={visibleUngroupedLists()}>
                        {(list) => {
                          const listIdx = () => ungroupedLists().indexOf(list);
                          return (
                            <ListRow
                              list={list}
                              todoCount={getTodoCount(list.id)}
                              indent={false}
                              getBgStyle={getBgStyle}
                              getTextColorStyle={getTextColorStyle}
                              layout={layout}
                              t={t}
                              index={listIdx()}
                              isCountsLoading={props.isCountsLoading}
                              searchQuery={searchQuery()}
                            />
                          );
                        }}
                      </For>
                    </div>

                    {/* Add list button at the bottom of ungrouped section */}
                    <button
                      type="button"
                      onClick={() => {
                        props.onAddList();
                      }}
                      class="flex items-center gap-3 py-2.5 pr-3 pl-4 w-full text-left rounded-xl hover:bg-foreground/[0.02] text-muted-foreground/60 hover:text-muted-foreground transition-colors group/add-row cursor-pointer"
                    >
                      <div class="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-muted-foreground/30 group-hover/add-row:border-muted-foreground/50 shrink-0">
                        <Plus class="h-4 w-4" />
                      </div>
                      <span class="text-xs font-semibold">{t(msg`Add list`)}</span>
                    </button>
                  </div>
                </div>
              </Show>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

interface HighlightTextProps {
  text: string | null;
  query: string;
}

function HighlightText(props: HighlightTextProps) {
  const parts = createMemo(() => {
    const textVal = props.text ?? "";
    const q = props.query.trim();
    if (!q) {
      return [textVal];
    }
    const escapedQuery = q.replaceAll(/[-/\\^$*+?.()|[\]{}]/g, String.raw`\$&`);
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    return textVal.split(regex);
  });

  return (
    <Show when={props.query.trim().length > 0} fallback={<span>{props.text}</span>}>
      <For each={parts()}>
        {(part) => {
          const isMatch = () => part.toLowerCase() === props.query.trim().toLowerCase();
          return (
            <Show when={isMatch()} fallback={part}>
              <span class="text-accent font-extrabold">{part}</span>
            </Show>
          );
        }}
      </For>
    </Show>
  );
}

// ─── Row Component representing an individual list ──────────────────────────
interface ListRowProps {
  list: ListWithMeta;
  todoCount: number;
  indent: boolean;
  getBgStyle: (color: string | null) => string;
  getTextColorStyle: (color: string | null) => string;
  layout: ReturnType<typeof useAppLayoutContext>;
  t: (message: string | MessageDescriptor, variables?: Record<string, unknown>) => string;
  index?: number;
  isCountsLoading?: boolean;
  searchQuery?: string;
}

function ListRow(props: ListRowProps) {
  const rowClass = () =>
    cn(
      "flex items-center gap-3 py-2.5 pr-3 rounded-xl transition-all duration-200",
      "hover:bg-foreground/[0.03] active:bg-foreground/[0.05] group/row w-full text-left relative min-w-0",
      props.indent ? "pl-10" : "pl-4",
    );

  return (
    <Link to="/lists/$listId" params={{ listId: props.list.id }} class={rowClass()}>
      {/* Squirrel Emoji Icon Badge */}
      <div
        class="flex h-8 w-8 items-center justify-center rounded-lg text-lg shrink-0 transition-transform group-hover/row:scale-105 duration-200"
        style={props.getBgStyle(props.list.color)}
      >
        <span style={props.getTextColorStyle(props.list.color)} class="font-semibold select-none">
          {props.list.icon ?? "📋"}
        </span>
      </div>

      <div class="flex-1 min-w-0">
        <h4 class="text-base font-semibold truncate text-foreground/90 group-hover/row:text-accent transition-colors">
          <HighlightText text={props.list.name} query={props.searchQuery ?? ""} />
        </h4>
      </div>

      <div class="flex items-center gap-2.5 shrink-0">
        <Show when={props.list.is_shared}>
          <Users class="h-4 w-4 text-success/80" />
        </Show>
        <CounterBadge
          count={props.isCountsLoading ? undefined : props.todoCount}
          showZero
          class="bg-muted-foreground/10 text-muted-foreground group-hover/row:bg-accent/15 group-hover/row:text-accent px-2 py-0.5 text-xs"
        />

        {/* Floating row action controls visible on hover */}
        <div class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity duration-200 bg-background/95 backdrop-blur-sm rounded-lg p-0.5 shadow-md border border-border/40">
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.layout.openShareListDialog(props.list);
            }}
            title={props.t(msg`Share list`)}
          >
            <Users class="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.layout.openEditListDialog(props.list);
            }}
            title={props.t(msg`Edit list`)}
          >
            <MoreVertical class="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </Link>
  );
}
